// -----------------------------------------------------------------------
// sessionState.js — namespaced localStorage helpers for "remember what I
// last had set" across every section of the app.
//
// Contract:
//   * Survives navigating away + refresh + browser restart.
//   * Cleared on logout (see AppShell.js — call `clearAllSessionState`).
//   * A `sanitize(state, ctx)` callback lets each page drop stale
//     references (a saved exchange that no longer exists, a filter that
//     references a deleted creator) BEFORE the state is applied.
//
// Values are namespaced under `kolradar:state:<section>` so we can clear
// them all at once on logout.
// -----------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";

const PREFIX = "kolradar:state:";
const key = (section) => `${PREFIX}${section}`;

function safeParse(raw) {
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export function loadSessionState(section, defaults) {
  const stored = safeParse(typeof localStorage !== "undefined" ? localStorage.getItem(key(section)) : null);
  if (!stored || typeof stored !== "object") return { ...(defaults || {}) };
  return { ...(defaults || {}), ...stored };
}

export function saveSessionState(section, state) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key(section), JSON.stringify(state));
  } catch { /* quota / disabled / ignore */ }
}

export function clearSectionState(section) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(key(section));
  } catch { /* ignore */ }
}

export function clearAllSessionState() {
  try {
    if (typeof localStorage === "undefined") return;
    const dead = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) dead.push(k);
    }
    dead.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

// -------------------------- React hook ----------------------------------
// Signature mirrors useState — pass the section id + defaults. The
// initialiser lazily reads storage exactly once; subsequent updates are
// debounced (250ms) so typing in a search box stays smooth even when the
// bundle is large.
const _pending = new Map(); // section -> { timer, state }
function _flush(section) {
  const p = _pending.get(section);
  if (!p) return;
  clearTimeout(p.timer);
  saveSessionState(section, p.state);
  _pending.delete(section);
}
export function scheduleSessionWrite(section, state, delay = 250) {
  const prev = _pending.get(section);
  if (prev) clearTimeout(prev.timer);
  const timer = setTimeout(() => {
    saveSessionState(section, state);
    _pending.delete(section);
  }, delay);
  _pending.set(section, { timer, state });
}
// Flush any pending writes on page hide / unload so refresh/close never
// loses the last few keystrokes.
if (typeof window !== "undefined" && !window.__krSessionUnloadWired) {
  window.__krSessionUnloadWired = true;
  const flushAll = () => {
    for (const section of Array.from(_pending.keys())) _flush(section);
  };
  window.addEventListener("beforeunload", flushAll);
  window.addEventListener("pagehide", flushAll);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushAll();
  });
}

export function usePersistedState(section, defaults) {
  const [state, setState] = useState(() => loadSessionState(section, defaults));
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    scheduleSessionWrite(section, state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const reset = () => {
    clearSectionState(section);
    setState({ ...(defaults || {}) });
  };
  return [state, setState, reset];
}
